'use client';

/**
 * HeaderActionsContext - Allows pages to inject custom actions into the header
 *
 * Usage in a page:
 * ```tsx
 * import { useHeaderActions } from '@/components/shell';
 *
 * function MyPage() {
 *   useHeaderActions([
 *     {
 *       id: 'my-action',
 *       label: 'Do Something',
 *       icon: <SomeIcon />,
 *       onClick: () => handleAction(),
 *       variant: 'primary',
 *     },
 *   ]);
 *
 *   return <div>Page content</div>;
 * }
 * ```
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import logger from '@/lib/logger';

// ============================================================================
// Types
// ============================================================================

export type HeaderActionVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'ghost';

export interface HeaderAction {
  /** Unique identifier for this action */
  id: string;

  /** Button label text */
  label: string;

  /** Icon component to display */
  icon?: React.ReactNode;

  /** Click handler */
  onClick?: () => void;

  /** Link href (if action should navigate) */
  href?: string;

  /** Button style variant */
  variant?: HeaderActionVariant;

  /** Whether the action is disabled */
  disabled?: boolean;

  /** Whether to show loading spinner */
  loading?: boolean;

  /** Priority for ordering (higher = further left, default = 0) */
  priority?: number;

  /** Tooltip text */
  tooltip?: string;

  /** Custom render function (overrides default button) */
  render?: () => React.ReactNode;

  /** Only show on desktop */
  desktopOnly?: boolean;

  /** Only show on mobile */
  mobileOnly?: boolean;
}

interface HeaderActionsContextValue {
  /** Current registered actions */
  actions: HeaderAction[];

  /** Register actions (call from useEffect) */
  registerActions: (sourceId: string, actions: HeaderAction[]) => void;

  /** Unregister actions by source ID */
  unregisterActions: (sourceId: string) => void;
}

// ============================================================================
// Context
// ============================================================================

const HeaderActionsContext = createContext<HeaderActionsContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface HeaderActionsProviderProps {
  children: React.ReactNode;
}

export function HeaderActionsProvider({ children }: HeaderActionsProviderProps) {
  // Store actions by source ID (allows multiple components to register)
  const [actionsBySource, setActionsBySource] = useState<Record<string, HeaderAction[]>>({});

  const registerActions = useCallback((sourceId: string, actions: HeaderAction[]) => {
    setActionsBySource((prev) => ({
      ...prev,
      [sourceId]: actions,
    }));
  }, []);

  const unregisterActions = useCallback((sourceId: string) => {
    setActionsBySource((prev) => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
  }, []);

  // Flatten and sort actions by priority (higher priority first)
  const actions = useMemo(() => {
    const allActions = Object.values(actionsBySource).flat();
    return allActions.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [actionsBySource]);

  const value = useMemo(
    () => ({ actions, registerActions, unregisterActions }),
    [actions, registerActions, unregisterActions]
  );

  return (
    <HeaderActionsContext.Provider value={value}>
      {children}
    </HeaderActionsContext.Provider>
  );
}

// ============================================================================
// Hook: useHeaderActionsContext
// ============================================================================

export function useHeaderActionsContext() {
  const context = useContext(HeaderActionsContext);
  if (!context) {
    throw new Error('useHeaderActionsContext must be used within HeaderActionsProvider');
  }
  return context;
}

// ============================================================================
// Hook: useHeaderActions
// ============================================================================

/**
 * Hook for pages to register header actions
 *
 * @param actions - Array of actions to register
 * @param deps - Dependency array (like useEffect) to trigger re-registration
 *
 * @example
 * ```tsx
 * useHeaderActions([
 *   { id: 'save', label: 'Save', onClick: handleSave, variant: 'primary' },
 *   { id: 'cancel', label: 'Cancel', onClick: handleCancel },
 * ], [handleSave, handleCancel]);
 * ```
 */
export function useHeaderActions(actions: HeaderAction[], deps: React.DependencyList = []) {
  const context = useContext(HeaderActionsContext);

  // Generate a stable source ID for this component instance
  const sourceIdRef = React.useRef<string>(`header-actions-${Math.random().toString(36).slice(2, 9)}`);

  // Extract stable function references from context
  // These are created with useCallback([]) in the provider, so they're stable
  const registerActions = context?.registerActions;
  const unregisterActions = context?.unregisterActions;

  useEffect(() => {
    // If no context (provider not mounted), silently skip
    if (!registerActions || !unregisterActions) {
      logger.warn('HeaderActionsContext', 'useHeaderActions: HeaderActionsProvider not found, actions will not be displayed');
      return;
    }

    const sourceId = sourceIdRef.current;

    // Debug logging for diagnosing registration issues
    logger.debug('HeaderActionsContext', 'Registering actions', {
      sourceId,
      actionCount: actions.length,
      actionIds: actions.map(a => a.id),
    });

    registerActions(sourceId, actions);

    return () => {
      logger.debug('HeaderActionsContext', 'Unregistering actions', { sourceId });
      unregisterActions(sourceId);
    };
    // actions is included because we use it inside the effect.
    // registerActions/unregisterActions are stable (from useCallback with []).
    // deps from caller allows explicit control over when to re-register.
  }, [registerActions, unregisterActions, actions, ...deps]);
}

// ============================================================================
// Hook: useHeaderActionsState (for reading actions in header)
// ============================================================================

/**
 * Hook for the header component to read registered actions
 */
export function useHeaderActionsState(): HeaderAction[] {
  const context = useContext(HeaderActionsContext);
  return context?.actions ?? [];
}

// ============================================================================
// Export
// ============================================================================

export default HeaderActionsContext;
